import { Linking, View } from 'react-native'
import React from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { InAppBrowser } from 'react-native-inappbrowser-reborn'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { Button, Icon, Text } from '@components/base'
import { ScrollableAvoidKeyboard } from '@components'
import { AppStackParamList, AuthStackParamList } from '@navigation/types'
import { UiColor, UiIcon } from '@src/types.ts'
import { userSelector } from '@selectors/app.ts'
import { getCoachDescriptor } from '@utils'
import { Analytics, CustomEventTypes } from '@config'

export const TrustpilotReview = () => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const route = useRoute<RouteProp<AuthStackParamList, 'TrustpilotReview'>>()
  const user = useSelector(userSelector)
  const coachDescriptor = getCoachDescriptor(user)

  const { url } = route.params

  const onShare = async () => {
    if (await InAppBrowser.isAvailable()) {
      InAppBrowser.open(url)
    } else {
      Linking.openURL(url)
    }
    Analytics.track(CustomEventTypes.TrustpilotReviewOpened, {
      userId: user?.id,
    })
    goToCompletedScreen()
  }

  const onDismiss = () => {
    Analytics.track(CustomEventTypes.TrustpilotReviewSkipped, {
      userId: user?.id,
    })
    goToCompletedScreen()
  }

  const goToCompletedScreen = () => {
    navigation.replace('CompletedScreen', {
      title: 'Feedback received',
      header: 'Thank you for your feedback!',
      introMarkdown: 'It will help us to continue to improve our product over time!',
      icon: UiIcon.SealCheck,
      iconColor: UiColor.Success,
      primaryActionText: `Back to ${coachDescriptor.capitalized.hubTitle}`,
      nextScreen: 'Drawer',
      nextScreenParams: { screen: 'Dashboard', params: { screen: 'NutritionistHub' } },
    })
  }

  return (
    <ScrollableAvoidKeyboard
      style={styles.container}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + 12, paddingBottom: insets.bottom + 16 },
      ]}
    >
      <View style={styles.texts}>
        <Icon
          name="seal-check"
          weight="fill"
          style={styles.icon}
          size={48}
          color="theme.success.base"
        />
        <Text type="title-2" style={[styles.text, styles.title]}>
          Share your experience
        </Text>
        <Text type="large" style={styles.text}>
          Thank you for sharing more about your recent dietitian call. Would you mind sharing your
          experience on Trustpilot to help others decide whether Nutrisense is right for them?
        </Text>
      </View>
      <View>
        <Button
          size="block"
          onPress={onShare}
          type="primary"
          accessibilityLabel="Share on Trustpilot"
        >
          Share on Trustpilot
        </Button>
        <Button size="block" onPress={onDismiss} type="transparent" accessibilityLabel="Dismiss">
          Dismiss
        </Button>
      </View>
    </ScrollableAvoidKeyboard>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  content: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 600,
    paddingHorizontal: 16,
  },
  texts: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    textAlign: 'center',
  },
  title: {
    maxWidth: 300,
    marginBottom: 8,
  },
  icon: {
    color: 'theme.success.base',
    marginBottom: 24,
  },
})
